import DocumentCard from "@components/DocumentCard/DocumentCard";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { formatDateTime } from "@utils/formatters/strings";
import { translateNormalizedRole } from "@utils/roles";
import { useTranslation } from "react-i18next";
import { useDownloadCertificate } from "../hooks/useDownloadCertificate";
import { LANG_BASE, service, TEST_ID_BASE } from "../VesselStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  submittedBy?: string;
  submittedByRole?: string;
  submittedAt?: string;
  externalNotes?: string;
  internalNotes?: string;
  externalAttachmentUrl?: string;
  externalAttachmentName?: string;
  internalAttachmentUrl?: string;
  internalAttachmentName?: string;
  statusChangeReason?: string;
  isRestrictionStatusSidedrawer?: boolean;
};

const DetailsSideDrawer = ({
  isOpen,
  onClose,
  submittedBy,
  submittedByRole,
  submittedAt,
  externalNotes,
  internalNotes,
  externalAttachmentUrl,
  externalAttachmentName,
  internalAttachmentUrl,
  internalAttachmentName,
  statusChangeReason,
}: Props) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const submittedByText = submittedBy;
  const submittedByRoleText = translateNormalizedRole(t, submittedByRole);
  const hasSubmittedByInfo = Boolean(submittedByText || submittedByRoleText);
  const submittedAtText = submittedAt ? formatDateTime(submittedAt) : "-";

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      keepMounted={false}
      title={t(`${LANG_BASE}.detailsTitle`)}
      hideCancelButton={true}
      submitLabel={t("common.ok")}
      onSubmit={onClose}
    >
      <div className="flex flex-col gap-4 w-[400px] p-6">
        {externalNotes && (
          <div className="col-span-full mb-2 flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.externalNotes`)}
            </CustomFormLabel>
            <p
              className="text-sm whitespace-pre-wrap wrap-break-word"
              data-testid={`${TEST_ID_BASE}-static-content`}
            >
              {externalNotes}
            </p>
          </div>
        )}
        {externalAttachmentUrl && (
          <div className="col-span-full mb-2 flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.attachments`)}
            </CustomFormLabel>
            <DocumentCard
              data-testid={`${TEST_ID_BASE}-external-attachment`}
              documentName={externalAttachmentName}
              fileName={""}
              onDownload={() => {
                handleDownload("document", service, externalAttachmentUrl, externalAttachmentName);
              }}
              onPreview={() => handlePreview("document", service, externalAttachmentUrl)}
            />
          </div>
        )}
        {internalNotes && (
          <div className="col-span-full mb-2 flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.internalNotes`)}
            </CustomFormLabel>
            <p
              className="text-sm whitespace-pre-wrap wrap-break-word"
              data-testid={`${TEST_ID_BASE}-static-content`}
            >
              {internalNotes}
            </p>
          </div>
        )}
        {internalAttachmentUrl && (
          <div className="col-span-full mb-0 flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.attachments`)}
            </CustomFormLabel>
            <DocumentCard
              data-testid={`${TEST_ID_BASE}-internal-attachment`}
              documentName={internalAttachmentName}
              fileName={""}
              onDownload={() => {
                handleDownload("document", service, internalAttachmentUrl, internalAttachmentName);
              }}
              onPreview={() => handlePreview("document", service, internalAttachmentUrl)}
            />
          </div>
        )}
        {statusChangeReason && (
          <div className="col-span-full mb-0 flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.statusReason`)}
            </CustomFormLabel>
            <p
              className="text-sm whitespace-pre-wrap wrap-break-word"
              data-testid={`${TEST_ID_BASE}-static-content`}
            >
              {statusChangeReason}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-500">
            <span>{t(`${LANG_BASE}.submitted-by`)}</span>{" "}
            {hasSubmittedByInfo ? (
              <>
                {submittedByText && <span className="text-gray-800">{submittedByText}</span>}
                {submittedByText && submittedByRoleText ? " / " : null}
                {submittedByRoleText}
              </>
            ) : (
              "-"
            )}
          </p>
          <p className="text-xs text-gray-500 mt-1">{submittedAtText}</p>
        </div>
      </div>
    </SideDrawer>
  );
};

export default DetailsSideDrawer;
